import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ReferenciaDoAviso, UserNotification } from '../../api/notificationsApi';
import BellIcon from '../../img/bell.svg';
import ClockIcon from '../../img/clock.svg';
import { useNotificacoes } from './useNotificacoes';
import { enderecoDaReferencia, rotuloDaReferencia } from './referenciaDoAviso';
import { notify } from '../../shared/feedback/notify';

import './sino-notificacoes.css';

/**
 * Família visual do aviso. A cor e o ícone são o que distingue um tipo do outro — **nunca emoji**
 * (regra do projeto). O sino do Chefe usava "🚨"/"🕒" inline até a at-107.
 */
function familiaDoAviso(tipo: string): { classe: string; icone: string; alt: string } {
  if (tipo === 'APPROACHING_SHOP_DEADLINE') {
    return { classe: 'prazo-oficina', icone: ClockIcon, alt: 'Prazo da oficina' };
  }
  if (tipo === 'APPROACHING_SECTOR_DEADLINE') {
    return { classe: 'prazo-etapa', icone: ClockIcon, alt: 'Prazo da etapa' };
  }
  if (
    tipo === 'COMENTARIO_NOVO' ||
    tipo === 'RESPOSTA_DE_COMENTARIO' ||
    tipo === 'COMENTARIO_COMPRA_NOVO' ||
    tipo === 'RESPOSTA_DE_COMENTARIO_COMPRA' ||
    tipo === 'MENCAO_EM_COMENTARIO'
  ) {
    return { classe: 'comentario', icone: BellIcon, alt: 'Comentário' };
  }
  return { classe: 'geral', icone: BellIcon, alt: 'Aviso' };
}

interface SinoNotificacoesProps {
  /**
   * Chamado quando o usuário clica num aviso que tem placa. Opcional de propósito: onde não há o
   * que fazer com a placa (a Sidebar, por exemplo), o clique só marca como lida e fecha.
   */
  onPlacaSelecionada?: (placa: string) => void;
  /**
   * at-129: chamado quando o aviso é de um PEDIDO ou de uma REQUISIÇÃO (comentário de compras).
   * Opcional pelo mesmo motivo do de placa: onde não há como abrir o item, o clique só marca lida.
   */
  onReferenciaSelecionada?: (tipo: ReferenciaDoAviso, id: string) => void;
  /** Na sidebar recolhida o dropdown precisa abrir para a direita para não sair da tela. */
  alinharEsquerda?: boolean;
  /**
   * transversal/at-90 — o sino ÚNICO do topo de todas as telas. Liga as três coisas que o tornam
   * notado (pedido do Victor, 23/10/2026: "loguei e não passou sensação de notificação nenhuma"):
   * um toast por aviso novo, um resumo ao entrar com avisos não lidos, e o contador no título da
   * aba do navegador. Só UMA instância pode tê-lo ligado — duas fariam toasts em dobro.
   */
  global?: boolean;
  /**
   * transversal/at-90: quem decide o destino do aviso inteiro (o sino do topo navega para a tela
   * do aviso). Quando presente, substitui `onPlacaSelecionada`/`onReferenciaSelecionada`.
   */
  aoAbrirAviso?: (notificacao: UserNotification) => void;
}

/** Chave da sessão do navegador: o resumo de "você tem N avisos" sai uma vez por aba. */
const CHAVE_DO_RESUMO = 'soac.sino.resumoMostrado';

function resumoJaMostrado(): boolean {
  try {
    return sessionStorage.getItem(CHAVE_DO_RESUMO) === '1';
  } catch {
    return false; // Silêncio deliberado: sem storage, o pior caso é o resumo aparecer de novo.
  }
}

function marcarResumoMostrado() {
  try {
    sessionStorage.setItem(CHAVE_DO_RESUMO, '1');
  } catch {
    // Silêncio deliberado: ver `resumoJaMostrado`.
  }
}

/** Até quantos avisos novos viram um toast cada; acima disso, um toast resume o resto. */
const MAXIMO_DE_TOASTS = 3;

/**
 * Sino de notificações do usuário logado (at-107).
 *
 * Vivia dentro de `ChefeDeOficina.tsx` — o único lugar do sistema onde uma notificação de usuário
 * aparecia. Com os comentários da at-90 avisando também o setor de EVENTOS (que não alcança
 * aquela tela), o sino virou peça compartilhada e passou a ser montado na Sidebar, valendo para
 * todos os papéis.
 */
const SinoNotificacoes: React.FC<SinoNotificacoesProps> = ({
  onPlacaSelecionada,
  onReferenciaSelecionada,
  alinharEsquerda = false,
  global = false,
  aoAbrirAviso,
}) => {
  const [aberto, setAberto] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const {
    notificacoes,
    total,
    naoLidas,
    carregandoMais,
    falhouCarga,
    fontesComFalha,
    carregado,
    carregarMais,
    marcarTodasComoLidas,
    marcarComoLida,
  } = useNotificacoes(aberto);
  /** Os avisos já anunciados nesta aba. `null` = a primeira carga ainda não chegou. */
  const vistos = useRef<Set<string> | null>(null);

  const aoClicarFora = useCallback((evento: MouseEvent) => {
    if (wrapperRef.current && !wrapperRef.current.contains(evento.target as Node)) {
      setAberto(false);
    }
  }, []);

  useEffect(() => {
    if (!aberto) return;
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [aberto, aoClicarFora]);

  const aoClicarNoAviso = async (notificacao: UserNotification) => {
    await marcarComoLida(notificacao);
    if (aoAbrirAviso) {
      aoAbrirAviso(notificacao);
    } else if (notificacao.referenciaTipo && notificacao.referenciaId && onReferenciaSelecionada) {
      onReferenciaSelecionada(notificacao.referenciaTipo, notificacao.referenciaId);
    } else if (notificacao.carPlate && onPlacaSelecionada) {
      onPlacaSelecionada(notificacao.carPlate);
    }
    setAberto(false);
  };

  /*
   * transversal/at-90 — os toasts do sino global.
   *
   * ⚠️ A PRIMEIRA carga não vira toast por aviso: quem entra com 30 não lidos receberia 30 toasts.
   * Ela vira um resumo só, uma vez por aba, e todo o resto passa a ser "já visto". Só o que chega
   * DEPOIS ganha toast próprio, com o clique levando ao mesmo destino do clique na lista.
   */
  useEffect(() => {
    if (!global || !carregado) return;
    if (vistos.current === null) {
      vistos.current = new Set(notificacoes.map((n) => n.id));
      if (naoLidas > 0 && !resumoJaMostrado()) {
        marcarResumoMostrado();
        notify.info(
          <span>
            Você tem <strong>{naoLidas}</strong> {naoLidas === 1 ? 'aviso não lido' : 'avisos não lidos'}.{' '}
            <span className="sino-toast-acao">Ver avisos</span>
          </span>,
          { toastId: 'sino-resumo', autoClose: 10000, onClick: () => setAberto(true) },
        );
      }
      return;
    }
    const novos = notificacoes.filter((n) => !n.read && !vistos.current!.has(n.id));
    novos.forEach((n) => vistos.current!.add(n.id));
    novos.slice(0, MAXIMO_DE_TOASTS).forEach((n) => {
      notify.info(
        <span>
          <strong>{n.title}</strong>
          <br />
          {n.message}
          <br />
          <span className="sino-toast-acao">
            {n.referenciaTipo ? rotuloDaReferencia(n.referenciaTipo) : 'Ver aviso'}
          </span>
        </span>,
        { toastId: `sino-aviso-${n.id}`, autoClose: 12000, onClick: () => void aoClicarNoAviso(n) },
      );
    });
    if (novos.length > MAXIMO_DE_TOASTS) {
      notify.info(`E mais ${novos.length - MAXIMO_DE_TOASTS} avisos novos no sino.`, {
        toastId: 'sino-mais-avisos',
        onClick: () => setAberto(true),
      });
    }
    // `aoClicarNoAviso` é recriada a cada render e não muda o que este efeito decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [global, carregado, notificacoes, naoLidas]);

  // transversal/at-90 — o contador no título da aba, para o aviso ser visto com o SOAC em segundo plano.
  useEffect(() => {
    if (!global) return undefined;
    const semContador = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = naoLidas > 0 ? `(${naoLidas}) ${semContador}` : semContador;
    return () => {
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
    };
  }, [global, naoLidas]);

  return (
    <div className="sino-notificacoes-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className={`sino-notificacoes-botao ${aberto ? 'open' : ''}${global && naoLidas > 0 ? ' com-avisos' : ''}`}
        onClick={() => setAberto((anterior) => !anterior)}
        aria-label={naoLidas > 0 ? `Notificações (${naoLidas} não lidas)` : 'Notificações'}
        title="Notificações"
      >
        <img src={BellIcon} alt="" />
        {naoLidas > 0 && <span className="sino-notificacoes-badge">{naoLidas}</span>}
      </button>

      {aberto && (
        <div
          className={`sino-notificacoes-dropdown ${alinharEsquerda ? 'alinhado-esquerda' : ''}`}
        >
          <div className="sino-notificacoes-cabecalho">
            <span className="sino-notificacoes-titulo">Notificações</span>
            {naoLidas > 0 && (
              <button
                type="button"
                className="sino-notificacoes-limpar"
                onClick={marcarTodasComoLidas}
              >
                Limpar tudo
              </button>
            )}
          </div>

          {/*
            at-60: com a carga falhada, a lista vazia parece "nenhuma notificação" — que é o
            oposto do que aconteceu. A faixa nega essa leitura antes de a lista aparecer.
          */}
          {falhouCarga && (
            <div className="sino-notificacoes-erro">
              Não foi possível carregar seus avisos
              {fontesComFalha.length ? ` ${fontesComFalha.join(' e ')}` : ''}. Pode haver notificações
              que não estão sendo exibidas. Recarregue a página; se persistir, acione a TI.
            </div>
          )}

          <div className="sino-notificacoes-lista">
            {notificacoes.length === 0 && !falhouCarga ? (
              <div className="sino-notificacoes-vazio">Nenhuma notificação no momento.</div>
            ) : (
              notificacoes.map((notificacao) => {
                const naoLida = !notificacao.read;
                const familia = familiaDoAviso(notificacao.type);

                const referencia =
                  notificacao.referenciaTipo && notificacao.referenciaId
                    ? { tipo: notificacao.referenciaTipo, id: notificacao.referenciaId }
                    : null;

                return (
                  <React.Fragment key={notificacao.id}>
                  <button
                    type="button"
                    onClick={() => aoClicarNoAviso(notificacao)}
                    className={`sino-notificacoes-item ${naoLida ? 'nao-lida' : ''}${referencia ? ' com-link' : ''}`}
                  >
                    {naoLida && <span className="sino-notificacoes-ponto" />}

                    <span className={`sino-notificacoes-icone ${familia.classe}`}>
                      <img src={familia.icone} alt={familia.alt} />
                    </span>

                    <span className="sino-notificacoes-conteudo">
                      <span className="sino-notificacoes-item-titulo">{notificacao.title}</span>
                      <span className="sino-notificacoes-item-mensagem">
                        {notificacao.message}
                      </span>
                      <span className="sino-notificacoes-item-data">
                        {new Date(notificacao.createdAt).toLocaleString('pt-BR')}
                      </span>
                    </span>
                  </button>
                  {/*
                    transversal/at-90 (pedido do Victor, 23/10/2026): o aviso de pedido/requisição
                    (menção, comentário, resposta) leva um texto sublinhado que abre o item em
                    OUTRA aba. Link de verdade, e não botão: o endereço existe, e o clique do meio
                    ou "abrir em nova janela" funcionam como em qualquer link.

                    ⚠️ Irmão do botão, nunca dentro dele — link dentro de botão é HTML inválido e
                    o clique dispararia os dois.
                  */}
                  {referencia ? (
                    <a
                      className="sino-notificacoes-abrir"
                      href={enderecoDaReferencia(referencia.tipo, referencia.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        void marcarComoLida(notificacao);
                      }}
                    >
                      {rotuloDaReferencia(referencia.tipo)}
                    </a>
                  ) : null}
                  </React.Fragment>
                );
              })
            )}

            {notificacoes.length < total && (
              <button
                type="button"
                className="sino-notificacoes-mais"
                onClick={carregarMais}
                disabled={carregandoMais}
              >
                {carregandoMais
                  ? 'Carregando...'
                  : `Carregar mais (${notificacoes.length} de ${total})`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SinoNotificacoes;
